import { useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Sparkles } from "lucide-react";
import { ContentSourceTabs } from "@/components/content-studio/ContentSourceTabs";
import { GenerationPanel } from "@/components/content-studio/GenerationPanel";
import type { NewsSource, SocialPostSource } from "@/types/content-studio";

export default function ContentStudio() {
  const [selectedNews, setSelectedNews] = useState<NewsSource[]>([]);
  const [selectedSocial, setSelectedSocial] = useState<SocialPostSource[]>([]);
  const [customInput, setCustomInput] = useState("");

  const handleAddNews = useCallback((source: NewsSource) => {
    setSelectedNews((prev) => (prev.some((s) => s.id === source.id) ? prev : [...prev, source]));
  }, []);
  const handleRemoveNews = useCallback((id: string) => {
    setSelectedNews((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const handleAddSocial = useCallback((source: SocialPostSource) => {
    setSelectedSocial((prev) => (prev.some((s) => s.post_id === source.post_id) ? prev : [...prev, source]));
  }, []);
  const handleRemoveSocial = useCallback((id: string) => {
    setSelectedSocial((prev) => prev.filter((s) => s.post_id !== id));
  }, []);

  const totalSources = selectedNews.length + selectedSocial.length + (customInput.length > 0 ? 1 : 0);

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-primary" />
            Content Studio
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Generate LinkedIn posts powered by Claude Sonnet &middot; CARR Biosystems brand voice
            {totalSources > 0 && (
              <Badge variant="secondary" className="ml-2 text-[10px]">
                {totalSources} source{totalSources !== 1 ? "s" : ""} selected
              </Badge>
            )}
          </p>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left: Source Selection (3 cols) */}
        <div className="lg:col-span-3">
          <ContentSourceTabs
            selectedNews={selectedNews}
            selectedSocial={selectedSocial}
            customInput={customInput}
            onAddNews={handleAddNews}
            onRemoveNews={handleRemoveNews}
            onAddSocial={handleAddSocial}
            onRemoveSocial={handleRemoveSocial}
            onCustomInputChange={setCustomInput}
          />
        </div>

        {/* Right: Generation Panel (2 cols) */}
        <div className="lg:col-span-2">
          <GenerationPanel
            selectedNews={selectedNews}
            selectedSocial={selectedSocial}
            selectedCampaigns={[]}
            customInput={customInput}
          />
        </div>
      </div>
    </div>
  );
}
